import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "antd";
import { RingLoader } from "react-spinners";

import Input from "../../../../components/input";
import Button from "../../../../components/button";
import Icon from "../../../../assets/icons";
import ProductDetails from "./product-details";
import ProductStepIndicator from "./indicator";
import { showToast } from "../../../../components/toast";
import useApi from "../../../../components/hooks/request";
import { useParams } from "react-router-dom";

interface AddProductDialogProps {
  isVisible: boolean;
  onClose: () => void;
  information?: any;
}

type FormStep = "productDetails" | "variants";

interface Variant {
  id: number;
  name: string;
  price: string;
  quantity: string;
  reorderLevel: string;
}

const AddProductDialog: React.FC<AddProductDialogProps> = ({
  isVisible,
  onClose,
  information,
}) => {
  const {id} = useParams()
  const [currentStep, setCurrentStep] = useState<FormStep>("productDetails");
  const [hasVariants, setHasVariants] = useState(false);
  const [variants, setVariants] = useState<Variant[]>([
    { id: Date.now(), name: "", price: "", quantity: "", reorderLevel: "" },
  ]);
  const [allVariantsSamePrice, setAllVariantsSamePrice] = useState(false);
  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
    clearErrors,
  } = useForm({
    mode: "onChange",
  });
  const { loading, makeRequest } = useApi(`/inventory/${id}`, "PUT");

  // Initialize hasVariants based on information.isVariant when component mounts or information changes
  useEffect(() => {
    if (information?.isVariant !== undefined) {
      setHasVariants(information.isVariant);
    }
  }, [information]);

  // Load existing variants if editing a product with variants
  useEffect(() => {
    if (information?.variant && Array.isArray(information.variant) && information.variant.length > 0) {
      const loadedVariants = information.variant.map((variant: any, index: number) => ({
        id: Date.now() + index, // Generate unique IDs
        name: variant.name || "",
        price: variant.price?.toString() || "",
        quantity: variant.quantity?.toString() || "",
        reorderLevel: variant.reorderLevel?.toString() || "",
      }));
      setVariants(loadedVariants);
    }
  }, [information]);

  console.log(id, "fuduuduud")

  const handleCreateProduct = handleSubmit(async (formData) => {
    const productData: any = {
      name: formData.name,
      description: formData.description,
      category: formData.category,
      measurementUnit: formData.measurementUnit,
      quantity: formData.quantity,
      isVariant: hasVariants,
    };

    // Only include variants if hasVariants is true
    if (hasVariants) {
      // Validate that all variants have required fields
      const isValidVariants = variants.every(variant => 
        variant.name.trim() && 
        variant.price.trim() && 
        variant.quantity.trim() && 
        variant.reorderLevel.trim()
      );

      if (!isValidVariants) {
        showToast("Please fill in all variant fields", false, {
          position: "top-right",
        });
        return;
      }

      productData.variant = variants.map((variant) => ({
        name: variant.name,
        price: Number(variant.price),
        quantity: Number(variant.quantity),
        reorderLevel: Number(variant.reorderLevel),
      }));
    } else {
      // Add single product price when no variants
      productData.price = Number(formData.price);
      productData.reorderLevel = Number(formData.reorderLevel);
    }

    const [response] = await makeRequest(productData);
    if (response.status) {
      showToast(response.message, true, { position: "top-right" });
      onClose();
      window.location.reload();
    } else {
      showToast(response?.message || "Failed to create product", false, {
        position: "top-right",
      });
    }
  });

  const validateAndProceed = async () => {
    const fieldsToValidate = [
      "name",
      "description",
      "category",
      "measurementUnit",
      "quantity",
    ];

    // Add price and reorderLevel validation when no variants
    if (!hasVariants) {
      fieldsToValidate.push("price", "reorderLevel");
    }

    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      if (hasVariants) {
        setCurrentStep("variants");
      } else {
        // Direct submission when no variants
        handleCreateProduct();
      }
    }
  };

  const addVariant = () => {
    const newVariant = {
      id: Date.now(),
      name: "",
      price: allVariantsSamePrice ? variants[0].price : "", // Copy price from first variant if "all same price" is checked
      quantity: "",
      reorderLevel: "",
    };

    setVariants([...variants, newVariant]);
  };

  const removeVariant = (variantId: number) => {
    if (variants.length > 1) {
      setVariants(variants.filter((variant) => variant.id !== variantId));
    }
  };

  const updateVariantField = (
    variantId: number,
    field: keyof Variant,
    value: string
  ) => {
    setVariants(
      variants.map((variant) => {
        // If updating price and all variants have same price, update all prices
        if (field === "price" && allVariantsSamePrice) {
          return { ...variant, price: value };
        }

        return variant.id === variantId
          ? { ...variant, [field]: value }
          : variant;
      })
    );
  };

  const renderVariantsStep = () => {
    return (
      <div className="space-y-4">
        {/* Checkbox for same price for all variants */}
        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="allSamePrice"
            checked={allVariantsSamePrice}
            onChange={(e) => {
              setAllVariantsSamePrice(e.target.checked);
              if (e.target.checked && variants.length > 0) {
                // Set all variant prices to the first variant's price
                const firstVariantPrice = variants[0].price;
                setVariants(prevVariants =>
                  prevVariants.map(variant => ({ ...variant, price: firstVariantPrice }))
                );
              }
            }}
            className="rounded border-gray-300"
          />
          <label htmlFor="allSamePrice" className="text-sm text-gray-700">
            All variants have the same price
          </label>
        </div>

        {variants.map((variant, index) => (
          <div key={variant.id} className="grid grid-cols-4 gap-3">
            <div>
              <Input
                value={variant.name}
                className="w-full"
                label={index === 0 ? "Variant" : ""}
                placeholder="Enter Variant Name"
                onChange={(e) =>
                  updateVariantField(variant.id, "name", e.target.value)
                }
                autoComplete="off"
              />
            </div>
            <div>
              <Input
                value={variant.price}
                className="w-full"
                label={index === 0 ? "Unit Price" : ""}
                placeholder="Enter Unit Price"
                type="number"
                onChange={(e) =>
                  updateVariantField(variant.id, "price", e.target.value)
                }
                // disabled={allVariantsSamePrice && index > 0}
                autoComplete="off"
              />
            </div>
            <div>
              <div className="flex items-center">
                <Input
                  value={variant.quantity}
                  className="w-full"
                  label={index === 0 ? "Quantity" : ""}
                  placeholder="1"
                  type="number"
                  onChange={(e) =>
                    updateVariantField(variant.id, "quantity", e.target.value)
                  }
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Input
                  value={variant.reorderLevel}
                  className="w-full"
                  label={index === 0 ? "Reorder Level" : ""}
                  placeholder="1"
                  type="number"
                  onChange={(e) =>
                    updateVariantField(
                      variant.id,
                      "reorderLevel",
                      e.target.value
                    )
                  }
                  autoComplete="off"
                />
              </div>
              {variants.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeVariant(variant.id)}
                  className="text-red-500 hover:text-red-700 mb-0.5"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m-6 0h6M6 10v10a2 2 0 002 2h8a2 2 0 002-2V10" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}

        <button
          type="button"
          className="flex items-center justify-center gap-1 text-green-600 font-medium mt-2 w-full"
          onClick={addVariant}
        >
          <span className="text-lg">+</span> Add New Variant
        </button>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "productDetails":
        return (
          <ProductDetails
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            hasVariants={hasVariants}
            setHasVariants={setHasVariants}
            information={information}
          />
        );
      case "variants":
        return renderVariantsStep();
      default:
        return (
          <ProductDetails
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            hasVariants={hasVariants}
            setHasVariants={setHasVariants}
            information={information}
          />
        );
    }
  };

  const renderFooterButtons = () => {
    if (currentStep === "productDetails") {
      return (
        <div className="flex gap-3 py-2 mt-4">
          <Button
            type="button"
            className="hover:bg-green-700 w-full justify-center"
            onClick={validateAndProceed}
            variant="primary"
            size="lg"
          >
            {hasVariants ? (
              <>
                <Icon name="noteIcon" />
                Proceed to Variants
              </>
            ) : (
              <>
                {loading ? (
                  <RingLoader color="#ffffff" loading={loading} size={20} />
                ) : (
                  <>Submit for Approval</>
                )}
              </>
            )}
          </Button>
        </div>
      );
    } else {
      return (
        <div className="flex justify-between gap-3 py-2 mt-4">
          <button
            type="button"
            className="bg-[#E8F2E8] w-full rounded-md hover:bg-gray-200 py-2 text-[#047401] flex justify-center items-center gap-2 font-medium"
            onClick={() => setCurrentStep("productDetails")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Product Details
          </button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="hover:bg-green-700 text-white w-full justify-center"
            onClick={handleCreateProduct}
          >
            {loading ? (
              <RingLoader color="#ffffff" loading={loading} size={20} />
            ) : (
              <>Submit for Approval</>
            )}
          </Button>
        </div>
      );
    }
  };

  return (
    <Modal
      visible={isVisible}
      onCancel={onClose}
       width="80%"
        centered={true}
        footer={null}
        style={{
          padding: 0,
          margin: 0,
          maxWidth: "1200px",
          height: "auto",
        }}
    >
      <h1 className="text-2xl font-bold -mt-2 mb-4">Edit Product</h1>
      <ProductStepIndicator
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        // hasVariants={hasVariants}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (currentStep === "productDetails") {
            validateAndProceed();
          } else {
            handleCreateProduct();
          }
        }}
      >
        <div className="grid gap-4">{renderStepContent()}</div>
        {renderFooterButtons()}
      </form>
    </Modal>
  );
};

export default AddProductDialog;